import { QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { logger } from '@/shared/utils/logger';

/**
 * React Query Configuration
 *
 * Global configuration for TanStack Query (React Query)
 * Used for server state management and data fetching
 */

// Constants
const CACHE_TIME_MINUTES = 5;
const MAX_RETRY_DELAY_MS = 30000;
const HTTP_STATUS_SERVER_ERROR_MIN = 500;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: How long data is considered fresh (5 seconds)
      staleTime: 5000,

      // Cache time: How long unused data stays in cache (5 minutes)
      gcTime: CACHE_TIME_MINUTES * 60 * 1000,

      // Retry failed requests 3 times with exponential backoff
      retry: 3,
      retryDelay: attemptIndex =>
        Math.min(1000 * 2 ** attemptIndex, MAX_RETRY_DELAY_MS),

      // Refetch on window focus
      refetchOnWindowFocus: true,

      // Refetch on mount if data is stale
      refetchOnMount: true,

      // Don't refetch on reconnect by default
      refetchOnReconnect: false,

      // Disable refetch interval by default
      refetchInterval: false,
    },
    mutations: {
      // Never retry 4xx errors — only retry network/5xx failures
      retry: (failureCount, error) => {
        if (
          axios.isAxiosError(error) &&
          error.response &&
          error.response.status < HTTP_STATUS_SERVER_ERROR_MIN
        ) {
          return false;
        }
        return failureCount < 1;
      },

      // Error handling
      onError: error => {
        logger.error(
          'Mutation error',
          error instanceof Error ? error : new Error(String(error)),
        );
      },
    },
  },
});

/**
 * Invalidate all queries
 * Useful after logout or when you need to clear all cached data
 */
export const invalidateAllQueries = () => {
  queryClient.invalidateQueries();
};

/**
 * Clear all query cache
 * More aggressive than invalidate - removes data from cache entirely
 */
export const clearQueryCache = () => {
  queryClient.clear();
};
